import { useEffect, useState } from "react";
import useTitleStore from "../../../../store/titleStore";
import useDebounce from "../../../../hooks/useDebounce";
import { useTableHeight } from "../../../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import {
  Card,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Pagination,
  Select,
  Table,
  Tag,
  Button,
  Image,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { rccPaymentProofService } from "../../../../services/rcc-payment-proof.service";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { API_BASE_URL } from "../../../../config/api.config";

export default function RccPaymentProofIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();

  const [messageApi, messageHolder] = message.useMessage();
  const savedState = JSON.parse(sessionStorage.getItem('rccPaymentProofState') || '{}');

  const [keyword, setKeyword] = useState(savedState.keyword || "");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [filterForm] = Form.useForm();

  const initialFormValues = {
    status: savedState.formValues?.status || null,
  };

  const [dataParams, setDataParams] = useState(savedState.dataParams || {
    limit: 10,
    page: 1,
    keyword: savedState.keyword || "",
    status: initialFormValues.status,
  });

  useEffect(() => {
    setTitle("Bukti Pembayaran RCC");
  }, [setTitle]);

  useEffect(() => {
    filterForm.setFieldsValue(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = Form.useWatch("status", filterForm);

  useEffect(() => {
    const statusValue = status !== undefined ? status : filterForm.getFieldValue('status');

    setDataParams((prev) => ({
      ...prev,
      keyword: debouncedKeyword || "",
      status: statusValue || null,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword, status]);

  useEffect(() => {
    sessionStorage.setItem('rccPaymentProofState', JSON.stringify({
      keyword,
      formValues: {
        status: filterForm.getFieldValue('status'),
      },
      dataParams
    }));
  }, [keyword, dataParams, filterForm]);

  const {
    data: informationData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["rccPaymentProof", dataParams],
    queryFn: () => rccPaymentProofService.getAllData(dataParams),
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (page, pageSize) => {
    setDataParams((prev) => ({
      ...prev,
      limit: pageSize,
      page,
    }));
  };

  const columns = [
    {
      title: "No.",
      align: "center",
      width: 80,
      fixed: breakpoint.md && "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Tracking ID",
      dataIndex: ["registration", "tracking_id"],
      width: 150,
      fixed: breakpoint.md && "left",
      render: (val) => <strong>{val || "-"}</strong>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (val) => {
        if (val === "updated") return <Tag color="green">Updated</Tag>;
        if (val === "cancel") return <Tag color="red">Cancel</Tag>;
        if (val === "in_progress") return <Tag color="orange">In Progress</Tag>;
        return "-";
      },
    },
    {
      title: "Tanggal Dibuat",
      dataIndex: "createdAt",
      width: 180,
      render: (val) => val && format(new Date(val), "dd/MM/yyyy HH:mm:ss"),
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 80,
      fixed: "right",
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="primary"
          variant="filled"
          onClick={() => navigate("/rcc/payment-proof/detail/" + record.id)}
        />
      ),
    },
  ];

  return (
    <>
      {messageHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Form form={filterForm} initialValues={initialFormValues}>
          <Flex
            justify={!breakpoint.lg ? "start" : "space-between"}
            align={!breakpoint.lg ? "start" : "center"}
            vertical={!breakpoint.lg}
            gap={!breakpoint.lg ? 16 : 0}
            style={{ paddingInline: 24, marginBottom: 24 }}
          >
            <Input
              style={{ width: 250 }}
              prefix={
                <Icon icon="hugeicons:search-01" width="16" height="16" />
              }
              placeholder="Cari Tracking ID..."
              onChange={(e) => setKeyword(e.target.value)}
              value={keyword}
            />

            <Flex gap={20}>
              <Form.Item noStyle name="status">
                <Select
                  allowClear
                  placeholder="Status"
                  style={{ width: 150 }}
                  options={[
                    { label: "In Progress", value: "in_progress" },
                    { label: "Updated", value: "updated" },
                    { label: "Cancel", value: "cancel" },
                  ]}
                />
              </Form.Item>
            </Flex>
          </Flex>
        </Form>

        <Table
          id="rcc-payment-proof-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={"id"}
          scroll={{ y: tableHeight }}
          dataSource={informationData?.data || []}
          pagination={false}
        />

        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={informationData?.pagination?.total || 0}
          showSizeChanger
          pageSizeOptions={["10", "20", "50"]}
          current={informationData?.pagination?.page || 1}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
    </>
  );
}
